import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { HTTP_RESPONSE_STATUS } from 'common/constants/httpMethodsConstants'

import { useFetchAppointmentChiefComplaintsQuery } from 'features/Schedule/api/chiefComplaints.api'
import { scheduleQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'
import { getErrorStatus, isNotSyncedMessage } from 'features/Schedule/api/chiefComplaints.transform'
import type { ChiefComplaintRow } from 'features/Schedule/api/chiefComplaints.types'
import { getIssueErrorMessage } from 'features/Schedule/api/medicalIssues.transform'

import type { ComplaintEntry, ComplaintListSnapshot } from './complaintField.types'
import {
  addComplaint,
  applyDraft,
  isComplaintListDirty,
  mergeServerRows,
  removeComplaint,
  rewordComplaint,
  toReplaceRows,
} from './complaintList'

// Strings copied from OpenEMR's chief_complaint_field/script.php, plus our own load and save texts.
export const COMPLAINT_TEXT = {
  label: 'Chief Complaint(s) / Reason(s) for Visit',
  placeholder: 'Enter complaint or click ...',
  inputTitle: 'The patient own words. Several are allowed.',
  patientGroup: "PATIENT'S PREVIOUS COMPLAINTS",
  practiceGroup: 'THE PRACTICE HEARS',
  nothing: 'Nothing recorded before - type it and press Enter.',
  choosePatient: 'Choose a patient first.',
  removeTitle: 'Take this off',
  rewordTitle: 'Click to correct the wording',
  checkedOut: 'the appointment is checked out',
  notInEmr: 'Complaints can be edited once this appointment reaches the EMR.',
  loadFailed: 'Chief complaints could not be loaded.',
} as const

// A server reason may already end its sentence.
export const lockLine = (reason: string): string =>
  `This can no longer be changed because ${reason.trim().replace(/\.+$/, '')}.`

type Args = {
  mode: 'create' | 'edit'
  siteId: string
  patientId: number | null
  appointmentId: number | null
  listRef: RefObject<ComplaintListSnapshot>
  // Words still in the box; they count at save time.
  draft?: string
}

const NO_ROWS: readonly ChiefComplaintRow[] = []

const readLoadHint = (error: unknown): string | null => {
  if (!error) return null
  if (getErrorStatus(error) !== HTTP_RESPONSE_STATUS.NOT_FOUND) return COMPLAINT_TEXT.loadFailed
  const message = getIssueErrorMessage(error, COMPLAINT_TEXT.loadFailed)
  return isNotSyncedMessage(message) ? COMPLAINT_TEXT.notInEmr : message
}

export const useChiefComplaints = ({
  mode,
  siteId,
  patientId,
  appointmentId,
  listRef,
  draft = '',
}: Args) => {
  const { setNotification } = useNotification()
  const isEdit = mode === 'edit'
  const skip = !isEdit || !appointmentId || !siteId
  // Asked again on every open: the rows are shared with the visit form and may have changed.
  const { data, isLoading, isFetching, error } = useFetchAppointmentChiefComplaintsQuery(
    { appointmentId: appointmentId ?? 0, siteId },
    { ...scheduleQueryOptions, skip },
  )
  // Until the fresh rows arrive, anything typed would be saved over them.
  const isPending = !skip && !error && (!data || !!isFetching)

  const [entries, setEntries] = useState<readonly ComplaintEntry[]>([])
  const [seed, setSeed] = useState<readonly ChiefComplaintRow[]>(NO_ROWS)
  const [rewordKey, setRewordKey] = useState<string | null>(null)
  const keyCounter = useRef(0)

  // A new patient in a new booking starts an empty list.
  useEffect(() => {
    if (isEdit) return
    setEntries([])
    setSeed(NO_ROWS)
    setRewordKey(null)
  }, [isEdit, patientId])

  // Server rows never overwrite an open reword or unsaved edits, unless the list froze.
  useEffect(() => {
    if (!data || (rewordKey && !data.frozen)) return
    setEntries((current) => mergeServerRows(current, seed, data))
    setSeed(data.complaints)
    if (data.frozen) setRewordKey(null)
    // seed is read as of the render this effect runs after; it is not a trigger for a rerun.
  }, [data, rewordKey])

  const frozen = data?.frozen ?? false
  const isUnreadable = data?.hasUnreadableRows ?? false
  // The save skips a list it must not write: frozen, or missing a row it could not read.
  const isLocked = frozen || isUnreadable

  useEffect(() => {
    const list = isLocked ? entries : applyDraft(entries, draft, rewordKey)
    listRef.current = {
      rows: toReplaceRows(list),
      dirty: isComplaintListDirty(list, seed),
      frozen: isLocked,
    }
  }, [entries, seed, isLocked, draft, rewordKey, listRef])

  const loadHint = error
    ? readLoadHint(error)
    : isUnreadable && !frozen
    ? COMPLAINT_TEXT.loadFailed
    : null

  // A 404 explains itself in the hint; anything else is worth a notification.
  useEffect(() => {
    if (loadHint !== COMPLAINT_TEXT.loadFailed) return
    setNotification({
      title: 'Chief complaints',
      description: COMPLAINT_TEXT.loadFailed,
      type: ALERT_CONSTANTS.ERROR,
    })
  }, [loadHint, setNotification])

  const add = useCallback((text: string) => {
    keyCounter.current += 1
    const key = `new-${keyCounter.current}`
    setEntries((current) => addComplaint(current, text, key))
  }, [])

  const remove = useCallback((key: string) => {
    setEntries((current) => removeComplaint(current, key))
    setRewordKey((current) => (current === key ? null : current))
  }, [])

  const finishReword = useCallback(
    (text: string) => {
      if (rewordKey) setEntries((current) => rewordComplaint(current, rewordKey, text))
      setRewordKey(null)
    },
    [rewordKey],
  )

  return {
    entries,
    frozen,
    frozenReason: data?.frozenReason ?? null,
    loadHint,
    isLoading,
    isPending,
    rewordKey,
    add,
    remove,
    startReword: setRewordKey,
    finishReword,
    cancelReword: useCallback(() => setRewordKey(null), []),
  }
}
